<script setup lang="ts">
import { api } from '@/api';
import {
  instanceOfDiscreteNumericValueDto,
  instanceOfEnumerationValueDto,
  instanceOfEnumerationValueListDto,
  instanceOfFaultyNumericValueDto,
  instanceOfMvTherapySystemSetupDto,
  instanceOfNumericListDto,
  instanceOfNumericValueDto,
  instanceOfTextValueDto,
  instanceOfTextValueListDto,
  EvaluationParameterScopeDto,
  ISingleSystemSetupParameter,
  MedicalSystemRtSystemDto,
  MedicalSystemSetupDto,
  MvTherapySystemSetupDto,
  ParameterCombinationDto,
  ParameterCombinationTableDto,
  QaEquipmentRtSystemDto,
  TaskItemAreaDto,
  TaskItemQaEquipmentSetupDto,
  TaskItemReferenceMeasurementResultsDto,
  TaskTypeDescription,
} from '@/api/generator';
import TimeStamp from '@/components/EntityHistory/components/TimeStamp.vue';
import Checkbox from '@/components/forms/Checkbox/Checkbox.vue';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import { useFormMode } from '@/components/forms/useFormMode.ts';
import Loader from '@/components/Loader/Loader.vue';
import ReferenceMeasurementDialog from '@/components/measurement/ReferenceMeasurementDialog.vue';
import { Measurement } from '@/components/measurement/types';
import { useReferenceMeasurement } from '@/components/measurement/utils/useReferenceMeasurement';
import Notification from '@/components/notifications/Notification.vue';
import { NotificationMessage } from '@/components/notifications/types';
import ParameterTable from '@/components/ParameterTable/ParameterTable.vue';
import { mainNavigationMedicalSystems, mainNavigationQaEquipment } from '@/components/icons/icons';
import TaskStepTitle from '@/components/TaskStepTitle/TaskStepTitle.vue';
import { provideAlignedTargetWidth } from '@/components/TaskStepTitle/useAlignedTargetWidth';
import { useTaskStepTitle } from '@/components/TaskStepTitle/useTaskStepTitles.ts';
import { getAssignedScopesByCombination } from '@/components/evaluation/scopes/combinationMatch';
import { truthy } from '@/utils/helper.ts';
import { scopeDisplayName } from '../utils';
import { useLoading } from '@/utils/useLoading';
import { useReferenceMeasurementInfo } from '@/utils/useReferenceMeasurementInfo.ts';
import {
  toCreateOrUpdateMedicalSystemSetupBaseDto,
  toCreateParameterCombinationTableDto,
  toCreateTaskItemQaEuipmentSetupDto,
} from '@/views/tasks/mapping.ts';
import { useTranslation } from 'i18next-vue';
import { isArray, isEmpty, isNil, isObject, keyBy, negate } from 'lodash';
import {
  useField,
  useFieldError,
  useFieldValue,
  useValidateField,
  useValidateForm,
} from 'vee-validate';
import { computed, onMounted, ref, toRef, watch, watchEffect } from 'vue';
import { checkIfBeamQualitiesAreSelected } from './SetupCombinationsUtil.ts';
import Button from '@/components/buttons/Button.vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import { getActionNotPossibleReason } from '@/utils/actions';
import { I18nKey } from '@/utils/typings';
import ReferenceMeasurementResults from './ReferenceMeasurementResults.vue';
import { ignoreValidationErrorHandler } from '@/errorHandling/errorHandler.ts';
import { AnyApiError, ApiError } from '@/errorHandling/typeGuards';
import SetupCombinationsUpdateNotification from './SetupCombinationsUpdateNotification.vue';
import TrackItFiltersPanel from './TrackItFiltersPanel.vue';
import TrackItFilterValueTable from './TrackItFilterValueTable.vue';
import TrackItMeasurementPickerDialog from '@/views/jobOccurrences/components/workItems/TrackItMeasurementPickerDialog.vue';
import { useTrackItFilters } from './useTrackItFilters';
import AssignedScopes from './AssignedScopes.vue';
import SetupCombinationRowIndicator from './SetupCombinationRowIndicator.vue';
import SetupCombinationsSelectionError from './SetupCombinationsSelectionError.vue';

const { t } = useTranslation();

const props = defineProps<{
  name: string;
  area: TaskItemAreaDto;
  medicalSystemSetup?: MvTherapySystemSetupDto | MedicalSystemSetupDto;
  medicalSystem?: MedicalSystemRtSystemDto;
  qaEquipmentSetups?: TaskItemQaEquipmentSetupDto[];
  qaEquipmentSystemEntities?: QaEquipmentRtSystemDto[];
  taskType: TaskTypeDescription;
}>();

const combinationTableError = useFieldError(toRef(() => `${props.name}.combinationTable`));

const hasCombinationTableError = computed(() => combinationTableError.value !== undefined);

function combinationFieldName(index: number) {
  return `${props.name}.combinationTable.combinations[${index}]`;
}

const { value: combinationTable, setValue: setCombinationTable } = useField<
  ParameterCombinationTableDto | null | undefined
>(() => `${props.name}.combinationTable`);

const evaluationScopes = useFieldValue<EvaluationParameterScopeDto[] | undefined>(
  () => `${props.name}.evaluationScopes`,
);

const assignedScopes = computed(() =>
  getAssignedScopesByCombination(
    evaluationScopes.value,
    combinationTable.value?.combinations ?? [],
  ),
);

function assignedScopeNames(combination: ParameterCombinationDto) {
  return (assignedScopes.value.get(combination) ?? []).map((scope) => scopeDisplayName(scope.name));
}

const combinationsPath = computed(() => `${props.name}.combinationTable.combinations`);

const validateCombinations = useValidateField(combinationsPath);

function setCombinationEnabled(index: number, isEnabled: boolean) {
  if (!combinationTable.value) return;

  combinationTable.value = {
    ...combinationTable.value,
    combinations: combinationTable.value.combinations?.map((combination, i) =>
      i === index ? { ...combination, isEnabled } : combination,
    ),
  };

  validateCombinations();
}

const mode = useFormMode();

const validate = useValidateForm();

const items = computed(() =>
  combinationTable.value?.combinations?.map((combination) => ({
    combination,
    parameters: keyBy(
      [...(combination.targetSystemValues || []), ...(combination.supportSystemValues || [])],
      'name',
    ),
  })),
);

const expandedIndices = ref<number[] | undefined>(undefined);

watch(
  items,
  (itemsNew) => {
    if (itemsNew?.length === 1 && combinationTable.value?.combinations?.length === 1) {
      expandedIndices.value = [0];
      combinationTable.value.combinations[0].isEnabled = true;
    }
  },
  { immediate: true },
);

const canGenerate = computed(() => {
  if (props.medicalSystemSetup && instanceOfMvTherapySystemSetupDto(props.medicalSystemSetup)) {
    return (
      unicheckSelected.value &&
      props.medicalSystemSetup &&
      props.medicalSystemSetup?.beamQualityContainer &&
      props.medicalSystemSetup.beamShapingContainer &&
      props.qaEquipmentSetups
    );
  } else {
    return props.medicalSystemSetup && props.qaEquipmentSetups;
  }
});

const parametersChanged = ref(false);

const unicheckSelected = ref(false);

const hasBeamShapings = computed(
  () =>
    props.medicalSystemSetup &&
    instanceOfMvTherapySystemSetupDto(props.medicalSystemSetup) &&
    props.medicalSystemSetup?.beamShapingContainer.isEnabled &&
    props.medicalSystemSetup?.beamShapingContainer.selectedBeamShapings.length,
);

function getParameterValue(param: ISingleSystemSetupParameter) {
  if (
    instanceOfNumericValueDto(param) ||
    instanceOfTextValueDto(param) ||
    instanceOfFaultyNumericValueDto(param) ||
    instanceOfDiscreteNumericValueDto(param) ||
    instanceOfEnumerationValueDto(param)
  ) {
    return param.value;
  }

  if (instanceOfNumericListDto(param) || instanceOfTextValueListDto(param)) {
    return param.values;
  }

  if (instanceOfEnumerationValueListDto(param)) {
    return param.selectedOptions;
  }
}

function hasParameterValue(param: ISingleSystemSetupParameter) {
  const value = getParameterValue(param);

  if (isArray(value)) {
    return value.some(negate(isNil));
  } else if (isObject(value)) {
    return !isEmpty(value);
  } else {
    return !isNil(value);
  }
}

function hasActiveParameterValue(param: ISingleSystemSetupParameter) {
  return !!param.isEnabled && hasParameterValue(param);
}

const hasTargetParameterWithValue = computed(
  () =>
    !!props.medicalSystemSetup?.singleSetupParameters.some(hasActiveParameterValue) ||
    hasBeamQualitiesSelected.value ||
    hasBeamShapings.value,
);

const hasSupportParameterWithValue = computed(() => {
  const params = props.qaEquipmentSetups?.flatMap((setup) => setup.singleSetupParameters);
  return params?.filter(truthy).some(hasActiveParameterValue);
});

const hasAnyParameterValue = computed(() =>
  Boolean(hasTargetParameterWithValue.value || hasSupportParameterWithValue.value),
);

const hasBeamQualitiesSelected = computed(
  () =>
    instanceOfMvTherapySystemSetupDto(props.medicalSystemSetup) &&
    checkIfBeamQualitiesAreSelected(props.medicalSystemSetup),
);

const hasCombinations = computed(() => !!items.value?.length);

const hasCombinationsWithAnyValue = computed(
  () => !!items.value?.flatMap((item) => !isEmpty(item.parameters)).every(Boolean),
);

const areCombinationsGenerated = computed(
  () => hasCombinations.value && !parametersChanged.value && canGenerate,
);

function checkForUnicheck() {
  const singleSetupParams = props.qaEquipmentSetups?.flatMap(
    (setup) => setup.singleSetupParameters || [],
  );

  unicheckSelected.value =
    singleSetupParams?.some((item) => item?.name?.includes('Orientation3d')) || false;
}

watch(
  [() => props.medicalSystemSetup?.singleSetupParameters, () => props.qaEquipmentSetups],
  () => {
    parametersChanged.value = true;

    checkForUnicheck();
  },
  { deep: true, immediate: false },
);

onMounted(checkForUnicheck);

const notification = ref<NotificationMessage>();
const unexpectedUpdateErrorNotification = ref<NotificationMessage>();

// Catches anything validationErrorHandler doesn't recognize, so it's surfaced, not dropped.
function unexpectedUpdateErrorHandler(_error: AnyApiError) {
  unexpectedUpdateErrorNotification.value = {
    type: 'error',
    message: t('SetupCombinationsUpdateFailedMessage' as I18nKey),
    closable: true,
  };
  return undefined;
}

const {
  trackItFilterCatalog,
  filtersFromCombinations: trackItFiltersFromCombinations,
  propertiesFromCombinations: trackItPropertiesFromCombinations,
  fetchSuggestedTrackItFiltersForModal,
  emptyTrackItPropertyValueErrorHandler,
  requestSuggestedTrackItParameterFilters,
  applyTrackItFilters,
} = useTrackItFilters(combinationTable, {
  unexpectedUpdateErrorNotification,
  applyUpdate: () => handleUpdateCombinationsAction(),
  areaId: props.area.id,
});

const hasTrackItFilterValues = computed(
  () =>
    props.taskType === 'TrackItImport' &&
    (trackItFiltersFromCombinations.value.length > 0 ||
      trackItPropertiesFromCombinations.value.length > 0),
);

async function handleUpdateCombinations(withValidation: boolean = true) {
  function updateCombinations(combinationsTable: ParameterCombinationTableDto) {
    expandedIndices.value = [];

    setCombinationTable(combinationsTable, true);

    parametersChanged.value = false;
  }

  function validationErrorHandler(error: AnyApiError) {
    // `getSetupCombinations` declares 422 as `Ptw100SetupCombinationsException`, not the generic
    // `UnprocessableContent`, and carries the table on its typed `combinationTable` property.
    if (error.name === ApiError.setupCombinations) {
      const exception = error.error?.exception;
      const i18n = exception?.i18n;

      notification.value = {
        type: 'error',
        message: t(i18n?.key as I18nKey),
        closable: false,
      };

      const combinationsTable = exception?.combinationTable;

      if (combinationsTable) {
        updateCombinations(combinationsTable);
      }

      return undefined;
    }

    if (error.name === ApiError.badRequest) {
      const exception = error.error?.exception;
      const i18n = exception?.validationErrors?.[0]?.i18n;

      notification.value = {
        type: 'error',
        title: t(exception?.message as I18nKey),
        message: i18n ? t(i18n.key as I18nKey, i18n?.parameters as unknown as string) : undefined,
        closable: false,
      };
    }

    return error;
  }

  const { result } = await api.taskItemsApi.getSetupCombinations(
    {
      getCombinationTableDto: {
        taskType: props.taskType,
        medicalSystemSetup: props.medicalSystemSetup
          ? toCreateOrUpdateMedicalSystemSetupBaseDto(props.medicalSystemSetup)
          : undefined,

        qaEquipmentSetups: props.qaEquipmentSetups
          ? props.qaEquipmentSetups.map((item) => toCreateTaskItemQaEuipmentSetupDto(item))
          : undefined,

        previousTable: combinationTable.value
          ? toCreateParameterCombinationTableDto(combinationTable.value)
          : undefined,
      },
    },
    [
      validationErrorHandler,
      ignoreValidationErrorHandler,
      emptyTrackItPropertyValueErrorHandler,
      unexpectedUpdateErrorHandler,
    ],
  );

  function resetNotifications() {
    notification.value = undefined;
    unexpectedUpdateErrorNotification.value = undefined;
  }

  if (result) {
    resetNotifications();
    updateCombinations(result);
  } else if (withValidation) {
    // Validate if combinationsTable was not generated to make user aware of potential invalid inputs
    validate();
  }
}

// triggered by the button
const { action: handleUpdateCombinationsAction, loading } = useLoading(handleUpdateCombinations);

const isReferenceMeasurementAllowed = computed(
  () => mode.value !== 'details' && !!combinationTable.value && !hasCombinationTableError.value,
);

const showMeasurementDialog = ref<boolean>(false);

const measurementsForDialog = ref<Measurement | undefined>(undefined);

watch(showMeasurementDialog, (showMeasurementDialogNew) => {
  if (showMeasurementDialogNew === false) {
    measurementsForDialog.value = undefined;
  }
});

const measurementResults = ref<Record<string, TaskItemReferenceMeasurementResultsDto | undefined>>(
  {},
);

function getResult(measurementId?: string) {
  return measurementId ? (measurementResults.value[measurementId] ?? undefined) : undefined;
}

async function addMeasurementResults(measurementId: string) {
  const { result } = await api.taskItemsApi.getTaskItemReferenceMeasurementResults({
    measurementId,
  });

  measurementResults.value[measurementId] = result;
}

watchEffect(async () => {
  const visibleMeasurementIds = expandedIndices.value
    ?.map(
      (index) =>
        combinationTable.value?.combinations?.[index].referenceMeasurementInfo?.measurementId,
    )
    .filter(truthy);

  for (const measurementId of visibleMeasurementIds ?? []) {
    await addMeasurementResults(measurementId);
  }
});

// All rows share one medical system width so the QA equipment icons line up.
provideAlignedTargetWidth();

const { getTitle } = useTaskStepTitle(
  computed<ParameterCombinationDto[]>(() => combinationTable.value?.combinations || []),
);

const { getNotification } = useReferenceMeasurementInfo(t);

const { openReferenceMeasurement, referenceMeasurement, isReferenceMeasurementDialogOpen } =
  useReferenceMeasurement();

// The combination as currently configured (including unsaved Track-it filter edits).
const matchingMeasurementsCombination = ref<ParameterCombinationDto>();
const matchingMeasurementsOpen = ref(false);

function showMatchingMeasurements(combination: ParameterCombinationDto) {
  matchingMeasurementsCombination.value = combination;
  matchingMeasurementsOpen.value = true;
}

const disabled = computed(() => {
  // disable in details mode or when the combination set has a validation error
  if (mode.value === 'details' || hasCombinationTableError.value) {
    return true;
  }

  // backend validation via possibleActions
  if (props.taskType === 'BeamConstancyCheck' && combinationTable.value?.possibleActions) {
    const executeAction = combinationTable.value.possibleActions.find(
      (action) => action.action === 'Execute',
    );
    return executeAction?.disabled ?? true;
  }

  return false;
});

const reasonI18nKey = computed(() => {
  const info = combinationTable.value
    ? getActionNotPossibleReason(combinationTable.value, 'Execute')
    : undefined;
  return info?.key ? (info.key as I18nKey) : undefined;
});

// Auto update combination table when no parameters are available
watch(hasAnyParameterValue, async (newValue, oldValue) => {
  if (oldValue && !newValue) {
    await handleUpdateCombinations(false);
  }
});

const initialCombinationsRequired = computed(() => {
  const hasNoCombinations = !combinationTable.value;

  switch (props.taskType) {
    case 'BeamConstancyCheck':
      return hasNoCombinations && hasBeamQualitiesSelected.value && unicheckSelected.value;
    case 'ManualTask':
    default:
      return hasNoCombinations && !!props.medicalSystemSetup;
  }
});

watch(
  initialCombinationsRequired,
  async (required) => {
    if (required) {
      await handleUpdateCombinations(false);
    }
  },
  { immediate: true },
);
</script>

<template>
  <div>
    <!-- Combinations changed and need update notification -->
    <SetupCombinationsUpdateNotification
      :name="`${name}.combinationTable`"
      @update="handleUpdateCombinationsAction"
    />

    <Notification
      v-if="mode !== 'details' && notification"
      :value="notification"
      class="mb-4 pa-4"
    />

    <Notification
      v-if="mode !== 'details' && unexpectedUpdateErrorNotification"
      :value="unexpectedUpdateErrorNotification"
      class="mb-4 pa-4"
      @click:close="unexpectedUpdateErrorNotification = undefined"
    />
    <SetupCombinationsSelectionError v-if="combinationTable" :name="combinationsPath" />

    <p
      v-if="!hasCombinationsWithAnyValue"
      class="text-body-1 text-primary-lighten-2 font-weight-semibold"
    >
      {{ $t('NoSetupParameterCombinationsMsg') }}
    </p>

    <template v-else>
      <p v-if="hasCombinations && canGenerate">
        {{ $t('SetupParameterCombinationsMsg') }}
      </p>

      <p v-if="areCombinationsGenerated && mode !== 'details'" class="mt-3">
        {{ $t('SetupParameterCombinationsGeneratedMsg') }}
      </p>

      <TrackItFiltersPanel
        v-if="props.taskType === 'TrackItImport' && hasCombinationsWithAnyValue"
        class="my-6"
        :combinations="combinationTable?.combinations"
        :trackItFilterCatalog="trackItFilterCatalog"
        :filtersFromCombinations="trackItFiltersFromCombinations"
        :propertiesFromCombinations="trackItPropertiesFromCombinations"
        :loadTrackItFilterSuggestions="fetchSuggestedTrackItFiltersForModal"
        @apply="applyTrackItFilters"
        @requestParameterSuggestions="requestSuggestedTrackItParameterFilters"
      />

      <Loader v-if="loading" type="content" />

      <v-expansion-panels
        v-else-if="hasCombinationsWithAnyValue"
        v-model="expandedIndices"
        class="mt-6"
        multiple
      >
        <v-expansion-panel
          v-for="({ combination }, index) in items"
          :key="`${combination.hash}-${index}`"
        >
          <v-expansion-panel-title v-if="(items?.length ?? 0) > 1">
            <div class="d-flex w-100 align-center">
              <Checkbox
                v-if="
                  typeof combinationTable?.combinations?.[index].isEnabled === 'boolean' &&
                  combinationTable?.combinations?.length !== 1
                "
                :modelValue="combinationTable.combinations[index].isEnabled"
                :name="`${name}.combinationTable.combinations[${index}].isEnabled`"
                :disabled="mode === 'details' || hasCombinationTableError"
                class="mr-2"
                hideDetails
                @update:modelValue="setCombinationEnabled(index, !!$event)"
                @click.stop
              />

              <TaskStepTitle
                :combination="getTitle(combination)"
                :targetName="medicalSystem?.name"
                mergeSets
              />

              <v-spacer />

              <AssignedScopes class="ml-2 mr-4" :names="assignedScopeNames(combination)" />

              <div v-if="taskType === 'BeamConstancyCheck'" class="d-inline-flex align-center">
                <div class="d-flex align-center">
                  <TimeStamp
                    v-if="combination.referenceMeasurementInfo?.creatorUser?.fullName"
                    :time="combination.referenceMeasurementInfo?.creationTime"
                    :user="combination.referenceMeasurementInfo.creatorUser.fullName"
                    type="created"
                    singleLine
                  />
                </div>

                <Notification
                  class="ml-2"
                  :value="
                    getNotification(
                      combination?.referenceMeasurementInfo?.referenceMeasurementState,
                    )
                  "
                />

                <Tooltip v-if="isReferenceMeasurementAllowed" :disabled="!reasonI18nKey">
                  <template #target="{ props: tooltipProps }">
                    <div v-bind="tooltipProps">
                      <Button
                        variant="secondary"
                        :disabled="disabled"
                        :label="$t('ReferenceMeasurement')"
                        size="small"
                        class="ml-2"
                        @click.stop="
                          openReferenceMeasurement(
                            combination,
                            medicalSystem,
                            qaEquipmentSystemEntities,
                          )
                        "
                      />
                    </div>
                  </template>
                  <template v-if="reasonI18nKey" #tooltip>
                    {{ $t(reasonI18nKey) }}
                  </template>
                </Tooltip>
              </div>

              <Button
                v-if="taskType === 'TrackItImport' && mode !== 'details'"
                variant="secondary"
                size="small"
                class="ml-2"
                :label="$t('ShowMatchingMeasurements' as I18nKey)"
                @click.stop="showMatchingMeasurements(combination)"
              />

              <SetupCombinationRowIndicator :name="combinationFieldName(index)" class="mx-2" />
            </div>
          </v-expansion-panel-title>

          <!-- Lazy by default (panel counts can run into the hundreds), except for the single-
               combination case below: it has no title (see v-if above), so there is nothing else
               to keep its row registered while collapsed - SetupCombinationRowIndicator in the
               title owns that job for every other row. Rows with Track-it filter fields stay
               mounted too: a lazy panel unmounts its fields on collapse, and vee-validate drops
               their validation errors with them. -->
          <v-expansion-panel-text :eager="(items?.length ?? 0) <= 1 || hasTrackItFilterValues">
            <InlineValidationError
              v-if="items?.length === 1"
              :name="combinationFieldName(index)"
              ignoreTouched
              class="mb-4"
            />

            <v-row v-if="items?.length === 1">
              <v-col>
                <div class="d-flex w-100 align-center">
                  <Checkbox
                    v-if="typeof combinationTable?.combinations?.[index].isEnabled === 'boolean'"
                    v-model="combinationTable.combinations[index].isEnabled"
                    :disabled="true"
                    :name="`${name}.combinationTable.combinations[${index}].isEnabled`"
                    class="mt-4"
                    hideDetails
                    @click.stop
                  />
                  <AssignedScopes class="ms-auto mr-4" :names="assignedScopeNames(combination)" />
                  <Button
                    v-if="taskType === 'TrackItImport' && mode !== 'details'"
                    variant="secondary"
                    size="small"
                    :class="{ 'ms-auto': !assignedScopeNames(combination).length }"
                    :label="$t('ShowMatchingMeasurements' as I18nKey)"
                    @click="showMatchingMeasurements(combination)"
                  />
                  <div
                    v-if="taskType === 'BeamConstancyCheck'"
                    :class="[
                      'd-inline-flex align-center',
                      { 'ms-auto': !assignedScopeNames(combination).length },
                    ]"
                  >
                    <div class="d-flex align-center">
                      <TimeStamp
                        v-if="combination.referenceMeasurementInfo?.creatorUser?.fullName"
                        :time="combination.referenceMeasurementInfo?.creationTime"
                        :user="combination.referenceMeasurementInfo.creatorUser.fullName"
                        type="created"
                        singleLine
                      />
                    </div>

                    <Notification
                      class="ml-2"
                      :value="
                        getNotification(
                          combination?.referenceMeasurementInfo?.referenceMeasurementState,
                        )
                      "
                    />

                    <Tooltip v-if="mode !== 'details'" :disabled="!reasonI18nKey">
                      <template #target="{ props: tooltipProps }">
                        <div v-bind="tooltipProps">
                          <Button
                            variant="secondary"
                            :disabled="disabled"
                            :label="$t('ReferenceMeasurement')"
                            class="ml-2"
                            size="small"
                            @click.stop="
                              openReferenceMeasurement(
                                combination,
                                medicalSystem,
                                qaEquipmentSystemEntities,
                              )
                            "
                          />
                        </div>
                      </template>
                      <template v-if="reasonI18nKey" #tooltip>
                        {{ $t(reasonI18nKey) }}
                      </template>
                    </Tooltip>
                  </div>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <div v-if="combination.targetSystemValues?.length">
                  <div class="d-flex align-center ga-3 mb-4">
                    <v-icon color="secondary" :icon="mainNavigationMedicalSystems" />
                    <h6 class="ma-0 text-primary">{{ $t('TargetSystemParameter_other') }}</h6>
                  </div>
                  <ParameterTable
                    showHeader
                    framedGroups
                    :parameters="combination.targetSystemValues"
                  />
                </div>
              </v-col>

              <v-col>
                <div v-if="combination.supportSystemValues?.length">
                  <div class="d-flex align-center ga-3 mb-4">
                    <v-icon color="secondary" :icon="mainNavigationQaEquipment" />
                    <h6 class="ma-0 text-primary">{{ $t('SupportSystemParameter_other') }}</h6>
                  </div>
                  <ParameterTable
                    showHeader
                    framedGroups
                    :parameters="combination.supportSystemValues"
                  />
                </div>
              </v-col>
            </v-row>

            <!-- In edit modes also without any Track-it data, as an empty state. -->
            <TrackItFilterValueTable
              v-if="hasTrackItFilterValues || (taskType === 'TrackItImport' && mode !== 'details')"
              class="mt-4"
              :name="combinationFieldName(index)"
              :combination="combination"
              :selectedFilters="trackItFiltersFromCombinations"
              :selectedProperties="trackItPropertiesFromCombinations"
              :disabled="combination.isEnabled === false"
            />

            <v-divider
              v-if="getResult(combination.referenceMeasurementInfo?.measurementId)"
              class="mt-4 mb-6"
            />

            <ReferenceMeasurementResults
              :result="getResult(combination.referenceMeasurementInfo?.measurementId)"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>

      <TrackItMeasurementPickerDialog
        v-if="matchingMeasurementsOpen && matchingMeasurementsCombination"
        v-model="matchingMeasurementsOpen"
        :combination="matchingMeasurementsCombination"
        :evaluationScopes="area.evaluationScopes ?? []"
        :trackItAlias="
          medicalSystem && 'trackItAlias' in medicalSystem ? medicalSystem.trackItAlias : undefined
        "
      />

      <ReferenceMeasurementDialog
        v-model:open="isReferenceMeasurementDialogOpen"
        :measurement="referenceMeasurement"
        @accepted="handleUpdateCombinationsAction(false)"
      />
    </template>
  </div>
</template>
